<script lang="ts">
	import "ckeditor5/ckeditor5-content.css";
	import clsx from "clsx";
	import type { ClassValue } from "svelte/elements";
	import { transformCkEditorContent } from "@core/utils/transformCkEditorContent.js";

	const {
		html,
		class: customClass,
		lightboxId = "content",
	}: {
		html: string;
		class?: ClassValue;
		lightboxId?: string;
	} = $props();

	/* eslint-disable svelte/no-at-html-tags */
</script>

{@html transformCkEditorContent(html, { customClass: clsx(customClass), lightboxId })}

<style lang="postcss" global>
	/* CkEditor content styly - aplikují se na zobrazený obsah */

	/* Základní styly pro CkEditor obsah */
	.ckeditor-content,
	:global(.ckeditor-content) {
		color: #202020 !important; /* správná barva textu */
		font-weight: 400;
		line-height: 1.35;
		font-size: 14px; /* text-md */
	}

	@media (min-width: 768px) {
		.ckeditor-content,
		:global(.ckeditor-content) {
			font-size: 0.875rem; /* md:text-p */
		}
	}

	@media (min-width: 1024px) {
		.ckeditor-content,
		:global(.ckeditor-content) {
			font-size: 1.5rem !important; /* lg:text-lg */
			line-height: 1.4 !important;
		}
	}

	/* Nadpisy podle general.css */
	.ckeditor-content h1,
	:global(.ckeditor-content h1) {
		color: #0000ff !important;
		margin-bottom: 1rem !important; /* 16px na mobilu */
		font-size: 2rem !important; /* text-2lg */
		line-height: 1.15 !important;
		font-weight: 400 !important; /* font-normal */
		letter-spacing: -0.01em !important; /* tracking-[-1%] */
	}

	@media (min-width: 640px) {
		.ckeditor-content h1,
		:global(.ckeditor-content h1) {
			font-size: 2.5rem !important; /* sm:text-xl */
		}
	}

	@media (min-width: 768px) {
		.ckeditor-content h1,
		:global(.ckeditor-content h1) {
			font-size: 4.5rem !important; /* md:text-3xl */
			margin-bottom: 2.5rem !important; /* 40px na desktop */
		}
	}

	.ckeditor-content h2,
	:global(.ckeditor-content h2) {
		color: #0000ff !important;
		margin-bottom: 1rem !important; /* 16px na mobilu */
		font-size: 27px !important; /* text-xl */
		font-weight: 300 !important; /* font-light */
		letter-spacing: -0.04em !important; /* tracking-[-4%] */
		line-height: 1.15 !important;
	}

	@media (min-width: 768px) {
		.ckeditor-content h2,
		:global(.ckeditor-content h2) {
			font-size: 3rem !important; /* md:text-2xl */
			margin-bottom: 2.5rem !important; /* 40px na desktop */
		}
	}

	.ckeditor-content h3,
	:global(.ckeditor-content h3) {
		color: #0000ff !important;
		margin-bottom: 1rem !important; /* 16px na mobilu */
		font-size: 1.5rem !important; /* text-lg */
		font-weight: 900 !important; /* font-black */
	}

	@media (min-width: 768px) {
		.ckeditor-content h3,
		:global(.ckeditor-content h3) {
			font-size: 2.5rem !important; /* md:text-xl */
			margin-bottom: 2.5rem !important; /* 40px na desktop */
		}
	}

	.ckeditor-content h4,
	:global(.ckeditor-content h4) {
		color: #0000ff !important;
		margin-bottom: 1rem !important; /* 16px na mobilu */
		margin-top: 1.5rem !important; /* 24px na mobilu */
		font-size: 1.375rem !important; /* text-2md */
		font-weight: 900 !important; /* font-black */
	}

	@media (min-width: 768px) {
		.ckeditor-content h4,
		:global(.ckeditor-content h4) {
			font-size: 1.5rem !important; /* md:text-lg */
			margin-bottom: 2.5rem !important; /* 40px na desktop */
			margin-top: 2rem !important; /* 32px na desktop */
		}
	}

	.ckeditor-content h5,
	.ckeditor-content h6,
	:global(.ckeditor-content h5),
	:global(.ckeditor-content h6) {
		color: #0000ff !important;
		margin-bottom: 1rem !important; /* 16px na mobilu */
		font-size: 1.25rem !important;
		font-weight: 500 !important;
	}

	@media (min-width: 768px) {
		.ckeditor-content h5,
		.ckeditor-content h6,
		:global(.ckeditor-content h5),
		:global(.ckeditor-content h6) {
			margin-bottom: 2.5rem !important; /* 40px na desktop */
		}
	} /* Paragrafy - podle skutečných stylů z webu */
	.ckeditor-content p,
	:global(.ckeditor-content p) {
		color: #202020 !important; /* správná barva textu */
		margin-bottom: 1rem !important; /* 16px na mobilu */
		font-weight: 400 !important; /* font-normal */
		font-size: 14px !important; /* text-md */
		line-height: 1.35 !important;
		letter-spacing: 0.015em !important; /* tracking-[0.015em] */
	}

	@media (min-width: 768px) {
		.ckeditor-content p,
		:global(.ckeditor-content p) {
			font-size: 0.875rem !important; /* md:text-p */
			margin-bottom: 1.5625rem !important; /* 25px na desktop */
		}
	}

	@media (min-width: 1024px) {
		.ckeditor-content p,
		:global(.ckeditor-content p) {
			font-size: 1.5rem !important; /* lg:text-lg */
			line-height: 1.4 !important;
		}
	}

	/* Seznamy - odrážkové s modrými spojovníky */
	.ckeditor-content ul,
	:global(.ckeditor-content ul) {
		margin-bottom: 1rem !important; /* 16px na mobilu */
		margin-top: 2rem !important; /* 32px na mobilu */
		list-style: none !important;
		padding-left: 0 !important;
		margin-left: 0rem !important; /* odstranění odsazení zleva */
	}

	@media (min-width: 768px) {
		.ckeditor-content ul,
		:global(.ckeditor-content ul) {
			margin-top: 4rem !important; /* 64px na desktop */
			margin-bottom: 1.5625rem !important; /* 25px na desktop */
		}
	}

	.ckeditor-content ul li,
	:global(.ckeditor-content ul li) {
		color: #202020 !important; /* správná barva textu */
		position: relative !important;
		margin-bottom: 0.5rem !important;
		padding-left: 1.5rem !important;
		font-weight: 400 !important; /* font-normal místo bold */
		font-size: 14px !important; /* text-md */
		line-height: 1.35 !important;
		letter-spacing: 0.015em !important;
	}

	@media (min-width: 768px) {
		.ckeditor-content ul li,
		:global(.ckeditor-content ul li) {
			font-size: 0.875rem !important; /* md:text-p */
		}
	}

	@media (min-width: 1024px) {
		.ckeditor-content ul li,
		:global(.ckeditor-content ul li) {
			font-size: 1rem !important; /* xl:text-base */
		}
	}

	.ckeditor-content ul li::before,
	:global(.ckeditor-content ul li::before) {
		content: "—" !important;
		color: #0000ff !important;
		position: absolute !important;
		left: 0 !important;
		top: 0 !important;
		font-weight: 700 !important;
	}

	/* Seznamy - číslované */
	.ckeditor-content ol,
	:global(.ckeditor-content ol) {
		margin-bottom: 1rem !important; /* 16px na mobilu */
		margin-top: 2rem !important; /* 32px na mobilu */
		list-style: decimal !important;
		padding-left: 1.5rem !important;
	}

	@media (min-width: 768px) {
		.ckeditor-content ol,
		:global(.ckeditor-content ol) {
			margin-top: 4rem !important; /* 64px na desktop */
			margin-bottom: 1.5625rem !important; /* 25px na desktop */
		}
	}

	.ckeditor-content ol li,
	:global(.ckeditor-content ol li) {
		color: #202020 !important; /* správná barva textu */
		margin-bottom: 0.5rem !important;
		font-weight: 400 !important; /* font-normal místo bold */
		font-size: 14px !important; /* text-md */
		line-height: 1.35 !important;
		letter-spacing: 0.015em !important;
	}

	@media (min-width: 768px) {
		.ckeditor-content ol li,
		:global(.ckeditor-content ol li) {
			font-size: 0.875rem !important; /* md:text-p */
		}
	}

	@media (min-width: 1024px) {
		.ckeditor-content ol li,
		:global(.ckeditor-content ol li) {
			font-size: 1rem !important; /* xl:text-base */
		}
	}

	/* Odkazy */
	.ckeditor-content a,
	:global(.ckeditor-content a) {
		color: #0000ff !important;
		text-decoration: underline !important;
	}

	.ckeditor-content a:hover,
	:global(.ckeditor-content a:hover) {
		text-decoration: none !important;
	}

	/* Zvýraznění textu */
	.ckeditor-content strong,
	:global(.ckeditor-content strong) {
		font-weight: 700 !important;
	}

	.ckeditor-content em,
	:global(.ckeditor-content em) {
		font-style: italic !important;
	}
</style>
